import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  PushNotificationSchema,
  Token,
  ActionPerformed,
  PushNotifications
} from '@capacitor/push-notifications'
import { FCM } from '@capacitor-community/fcm';
import { PushNotificationService } from 'ticket-sdk';
import { take, takeWhile } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController, Platform } from '@ionic/angular';
import { environment } from '@env';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PushService {
  androidToken: Token;

  constructor(
    private pushNotificationService: PushNotificationService, 
    private alertController: AlertController
  ) { }

  addUserToken() {
    if (Capacitor.getPlatform() === 'android' && !!this.androidToken) {
      this.pushNotificationService.addUserToken(this.androidToken.value).pipe(take(1)).subscribe(() => { }, (error: HttpErrorResponse) => { console.log(error.statusText + ' ' + error.message) });
    } else {
      FCM.getToken().then((data) => {
        this.pushNotificationService.addUserToken(data.token).pipe(take(1)).subscribe(() => { }, (error: HttpErrorResponse) => { console.log(error.statusText + ' ' + error.message) });
      })
    }
  }

  private listen() {
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration',
      (token: Token) => {
        this.androidToken = token;
        console.log('Push registration success, token: ' + token.value);
      }
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError',
      (error: any) => {
        console.log('Error on registration: ' + JSON.stringify(error));
      }
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        this.presentAlert(notification.title, notification.body);
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        console.log('Push action performed: ' + JSON.stringify(notification));
      }
    );
  }

  async clearNotifications() {
    await PushNotifications.removeAllDeliveredNotifications();
  }

  async initiate() {
    const permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt' || permStatus.receive === 'granted') {
      // Request Push Notification permission. 
      const request = await PushNotifications.requestPermissions();

      if (request.receive === 'granted') {
        // The user has granted permission to register to APNS/FCM. 
        PushNotifications.register().then(() => {
          FCM
            .subscribeTo({ topic: environment.situationsTopic })
            .then((r) => console.log(`subscribed to topic situations`))
            .catch((err) => console.log(err));
        });
  
      } else {
        // The user has rejected our request to register with APNS/FCM. 
        await FCM.deleteInstance();
        this.pushNotificationService.removeUserToken();
      }
    }

    this.clearNotifications();
    this.listen();
  }

  async presentAlert(header: string, message: string) {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: ['OK']
    });

    await alert.present();
  }

  async removeUserToken() {
    await FCM.deleteInstance();
    this.pushNotificationService.removeUserToken();
  }
}
